/**
 * Консервация оборудования на хранении (СК-38): на карточке экземпляра — условия хранения, сроки работ по
 * требованиям изготовителя, кнопка «Работа выполнена» с фото, история; на карточке позиции каталога —
 * условия и список периодических работ (правит кладовщик компании).
 */
import {
  type FileInfo,
  PRESERVATION_ASSIGNEES,
  type PreservationAssignee,
  type PreservationCard,
  type PreservationProfile,
  type PreservationProfileInput,
  type PreservationRow,
  type PreservationWorkInput,
  type ToolUnit,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const STATE_TONE: Record<PreservationRow['state'], 'success' | 'warning' | 'danger'> = {
  ok: 'success',
  soon: 'warning',
  overdue: 'danger',
};
const STATE_LABEL: Record<PreservationRow['state'], string> = {
  ok: 'в сроке',
  soon: 'срок подходит',
  overdue: 'просрочено',
};

export const preservationQuery = (unitId: string) => ({
  queryKey: ['wh', 'preservation', unitId],
  queryFn: () => api<PreservationCard>(`/warehouse/tools/${unitId}/preservation`),
});

/** Блок консервации на карточке экземпляра */
export function PreservationPanel({ u }: { u: ToolUnit }) {
  const q = useQuery(preservationQuery(u.id));
  const [work, setWork] = useState<PreservationRow | null>(null);
  const qc = useQueryClient();
  const c = q.data;
  if (q.isPending)
    return (
      <Panel title="Консервация на хранении">
        <Spinner />
      </Panel>
    );
  if (!c || (!c.rows.length && !c.works.length && !c.storageConditions)) return null;
  return (
    <Panel
      title="Консервация на хранении"
      meta={u.mechanicName ? `механик — ${u.mechanicName}` : 'механик не назначен'}
    >
      <div className="flex flex-col gap-3 text-[14px]">
        {c.storageConditions ? (
          <p>
            <span className="text-ink-2">Условия хранения: </span>
            {c.storageConditions}
          </p>
        ) : null}
        {!c.inStorage ? (
          <p className="text-ink-2">
            Экземпляр не на хранении (на руках, у подрядчика, в ремонте или списан) — работы по консервации не
            ставятся.
          </p>
        ) : null}
        {c.rows.length ? (
          <ul className="flex flex-col gap-2">
            {c.rows.map((r) => (
              <li
                key={r.rule.id}
                className="flex flex-col gap-2 rounded-md bg-surface-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{r.rule.title}</span>
                  <Badge tone={STATE_TONE[r.state]} className="ml-2">
                    {STATE_LABEL[r.state]}
                  </Badge>
                  <div className="text-[13px] text-ink-2">
                    раз в {r.rule.intervalDays} дн. · {r.assigneeName} · срок {date(r.dueOn)}
                    {r.lastDoneOn ? ` · последняя ${date(r.lastDoneOn)}` : ' · работ ещё не было'}
                    {r.rule.instructions ? ` · ${r.rule.instructions}` : ''}
                  </div>
                </div>
                {u.can.preserve ? (
                  <Button
                    variant={r.state === 'ok' ? 'secondary' : 'primary'}
                    className="w-full sm:w-auto"
                    onClick={() => setWork(r)}
                  >
                    Работа выполнена
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {c.works.length ? (
          <div>
            <h3 className="text-[13px] font-semibold text-ink">Выполненные работы</h3>
            <ul className="mt-1 flex flex-col gap-1 text-[13px]">
              {c.works.map((w) => (
                <li key={w.id}>
                  {date(w.doneOn)} · {w.ruleTitle}
                  {w.doneByName ? ` · ${w.doneByName}` : ''}
                  {w.note ? ` · ${w.note}` : ''}
                  {w.fileId && u.can.files ? (
                    <>
                      {' · '}
                      <a
                        href={`/api/v1/files/${w.fileId}/download`}
                        className="text-accent-text [overflow-wrap:anywhere] hover:underline"
                      >
                        {w.fileName ?? 'фото'}
                      </a>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="text-[12px] text-ink-2">
          Условия и перечень работ — в{' '}
          <Link to="/warehouse/items/$itemId" params={{ itemId: u.itemId }} className="underline">
            позиции каталога
          </Link>
          . Просроченная консервация попадает в «Требует внимания» проекта склада.
        </p>
      </div>
      {work ? (
        <WorkDialog
          u={u}
          row={work}
          onClose={() => setWork(null)}
          onDone={() => {
            setWork(null);
            qc.invalidateQueries({ queryKey: ['wh'] });
          }}
        />
      ) : null}
    </Panel>
  );
}

function WorkDialog({
  u,
  row,
  onClose,
  onDone,
}: {
  u: ToolUnit;
  row: PreservationRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [f, setF] = useState({ doneOn: todayLocal(), note: '' });
  const [file, setFile] = useState<FileInfo | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('entityType', 'wh_tool_unit');
      form.append('entityId', u.id);
      form.append('file', file);
      return api<FileInfo>('/files', { form, method: 'POST' });
    },
    onSuccess: (f) => setFile(f),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const m = useMutation({
    mutationFn: () => {
      const body: PreservationWorkInput = {
        ruleId: row.rule.id,
        doneOn: f.doneOn,
        note: f.note.trim() || null,
        fileId: file?.id ?? null,
      };
      return api<PreservationCard>(`/warehouse/tools/${u.id}/preservation`, { body });
    },
    onSuccess: () => {
      toast('Работа по консервации записана в карточку');
      onDone();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`${row.rule.title} — ${u.inventoryNumber}`}
      description={row.rule.instructions ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!f.doneOn} onClick={() => m.mutate()}>
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Дата работы">
          <Input
            type="date"
            max={todayLocal()}
            value={f.doneOn}
            onChange={(e) => setF({ ...f, doneOn: e.target.value })}
          />
        </Field>
        <Field label="Что сделано" hint="показания, замечания, кто помогал">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <Field
          label="Фото"
          hint={
            u.can.edit
              ? 'подтверждение факта — остаётся во вложениях экземпляра'
              : 'приложить фото может кладовщик склада; факт без фото тоже записывается'
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={`inline-flex min-h-11 items-center gap-1.5 rounded-md border border-line px-3 text-[13px] font-medium text-ink ${u.can.edit ? 'cursor-pointer hover:bg-tile' : 'cursor-not-allowed opacity-50'}`}
            >
              <input
                type="file"
                disabled={!u.can.edit}
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  const x = e.target.files?.[0];
                  if (x) upload.mutate(x);
                  e.target.value = '';
                }}
              />
              <Paperclip className="size-4" aria-hidden />
              {upload.isPending ? 'Загрузка…' : file ? 'Заменить фото' : 'Приложить фото'}
            </label>
            {file ? <span className="text-[13px] text-ink-2">{file.name}</span> : null}
          </div>
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Вложения экземпляра: фото консервации, паспорт, акты — общий модуль файлов, вид `wh_tool_unit` */
export function UnitFilesPanel({ u }: { u: ToolUnit }) {
  const qc = useQueryClient();
  const key = ['files', 'wh_tool_unit', u.id];
  const files = useQuery({
    queryKey: key,
    queryFn: () => api<FileInfo[]>('/files', { query: { entityType: 'wh_tool_unit', entityId: u.id } }),
    enabled: u.can.files,
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('entityType', 'wh_tool_unit');
      form.append('entityId', u.id);
      form.append('file', file);
      return api<FileInfo>('/files', { form, method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      toast('Файл приложен к экземпляру');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (!u.can.files) return null;
  return (
    <Panel
      title="Вложения"
      meta="паспорт, фото хранения и консервации"
      actions={
        u.can.edit ? (
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border border-line px-3 text-[13px] font-medium text-ink hover:bg-tile md:min-h-9">
            <input
              type="file"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <Paperclip className="size-4" aria-hidden />
            {upload.isPending ? 'Загрузка…' : 'Приложить файл'}
          </label>
        ) : null
      }
    >
      {files.isPending ? (
        <Spinner />
      ) : files.data?.length ? (
        <ul className="flex flex-col gap-1 text-[14px]">
          {files.data.map((f) => (
            <li key={f.id}>
              <a
                href={`/api/v1/files/${f.id}/download`}
                className="text-accent-text [overflow-wrap:anywhere] hover:underline"
              >
                {f.name}
              </a>
              <span className="text-[12px] text-ink-2">
                {' '}
                · {date(f.createdAt)}
                {f.uploadedBy ? ` · ${f.uploadedBy}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">Файлов нет.</p>
      )}
    </Panel>
  );
}

/** Требования изготовителя к хранению на карточке позиции каталога */
export function ItemPreservationPanel({ itemId }: { itemId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['wh', 'item-preservation', itemId],
    queryFn: () => api<PreservationProfile>(`/warehouse/items/${itemId}/preservation`),
  });
  const [edit, setEdit] = useState(false);
  const p = q.data;
  if (q.isPending || !p) return null;
  if (!p.rules.length && !p.storageConditions && !p.canEdit) return null;
  return (
    <Panel
      title="Хранение и консервация"
      meta="требования изготовителя (СК-38)"
      actions={p.canEdit ? <Button onClick={() => setEdit(true)}>Править</Button> : null}
    >
      <div className="flex flex-col gap-2 text-[14px]">
        <p>
          <span className="text-ink-2">Условия хранения: </span>
          {p.storageConditions ?? 'не заданы'}
        </p>
        {p.rules.length ? (
          <ul className="flex flex-col gap-1">
            {p.rules.map((r) => (
              <li key={r.id}>
                <span className="font-medium">{r.title}</span> — раз в {r.intervalDays} дн.,{' '}
                {PRESERVATION_ASSIGNEES[r.assignee].toLowerCase()}
                {r.instructions ? <span className="text-ink-2"> · {r.instructions}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-2">
            Периодических работ нет. Для насосов, мембран, КИП и щитов задайте работы — портал будет ставить
            их кладовщику или механику по каждому экземпляру на хранении.
          </p>
        )}
      </div>
      {edit ? (
        <ProfileDialog
          itemId={itemId}
          initial={p}
          onClose={() => setEdit(false)}
          onDone={() => {
            setEdit(false);
            qc.invalidateQueries({ queryKey: ['wh'] });
          }}
        />
      ) : null}
    </Panel>
  );
}

type RuleDraft = {
  k: string;
  id?: string;
  title: string;
  intervalDays: string;
  assignee: PreservationAssignee;
  instructions: string;
};

function ProfileDialog({
  itemId,
  initial,
  onClose,
  onDone,
}: {
  itemId: string;
  initial: PreservationProfile;
  onClose: () => void;
  onDone: () => void;
}) {
  const [conditions, setConditions] = useState(initial.storageConditions ?? '');
  const [rules, setRules] = useState<RuleDraft[]>(
    initial.rules.map((r) => ({
      k: r.id,
      id: r.id,
      title: r.title,
      intervalDays: String(r.intervalDays),
      assignee: r.assignee,
      instructions: r.instructions ?? '',
    })),
  );
  const [seq, setSeq] = useState(0);
  const add = () => {
    setRules([
      ...rules,
      { k: `new-${seq}`, title: '', intervalDays: '30', assignee: 'storekeeper', instructions: '' },
    ]);
    setSeq(seq + 1);
  };
  const update = (k: string, patch: Partial<RuleDraft>) =>
    setRules((rs) => rs.map((r) => (r.k === k ? { ...r, ...patch } : r)));
  const valid = rules.every((r) => r.title.trim().length >= 2 && Number(r.intervalDays) > 0);
  const m = useMutation({
    mutationFn: () => {
      const body: PreservationProfileInput = {
        storageConditions: conditions.trim() || null,
        rules: rules.map((r) => ({
          ...(r.id ? { id: r.id } : {}),
          title: r.title.trim(),
          intervalDays: Number(r.intervalDays),
          assignee: r.assignee,
          instructions: r.instructions.trim() || null,
        })),
      };
      return api<PreservationProfile>(`/warehouse/items/${itemId}/preservation`, { method: 'PUT', body });
    },
    onSuccess: () => {
      toast('Требования к хранению сохранены');
      onDone();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Хранение и консервация"
      description="Условия и периодические работы по требованиям изготовителя — на все экземпляры позиции. Убранная работа перестаёт ставиться, история остаётся"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!valid} onClick={() => m.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Условия хранения" hint="сухое отапливаемое, консервирующий раствор, температура">
          <Textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} />
        </Field>
        <div className="flex flex-col gap-3">
          {rules.map((r) => (
            <div
              key={r.k}
              className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-[1fr_110px_170px]"
            >
              <Field label="Работа">
                <Input
                  value={r.title}
                  placeholder="Проворот вала на 1,5 оборота"
                  onChange={(e) => update(r.k, { title: e.target.value })}
                />
              </Field>
              <Field label="Раз в, дней">
                <Input
                  inputMode="numeric"
                  value={r.intervalDays}
                  onChange={(e) => update(r.k, { intervalDays: e.target.value })}
                />
              </Field>
              <Field label="Кому">
                <Select
                  value={r.assignee}
                  onChange={(e) => update(r.k, { assignee: e.target.value as PreservationAssignee })}
                >
                  {(Object.keys(PRESERVATION_ASSIGNEES) as PreservationAssignee[]).map((k) => (
                    <option key={k} value={k}>
                      {PRESERVATION_ASSIGNEES[k]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Как выполнять" className="sm:col-span-2">
                <Input
                  value={r.instructions}
                  onChange={(e) => update(r.k, { instructions: e.target.value })}
                />
              </Field>
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setRules(rules.filter((x) => x.k !== r.k))}
                >
                  Убрать
                </Button>
              </div>
            </div>
          ))}
          <Button variant="secondary" onClick={add} disabled={rules.length >= 20}>
            + Добавить работу
          </Button>
        </div>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
